/**
 * Modal Component
 *
 * Standardized modal dialog for Admin UI.
 * Handles overlay, keyboard interactions, and layout.
 * Supports premium mode with enhanced animations.
 *
 * @package MultilingualPressZone
 */

import { el, clear, __ } from '../utils/dom.js';

export default class Modal {
    constructor(options = {}) {
        this.options = {
            title: '',
            width: '600px',
            id: 'mpz-modal',
            containerId: 'mpz-modal-container',
            onClose: null,
            premium: false, // Enable premium styling
            icon: null, // Optional title icon
            headerAction: null, // Optional action button for header
            ...options
        };


        this.container = document.getElementById(this.options.containerId);
        if (!this.container) {
            // Create if missing (fallback) - this happens when Modal is instantiated before App()
            this.container = el('div', { id: this.options.containerId });
            document.body.appendChild(this.container);
        }

        // Add admin class for styling
        this.container.classList.add('mpz-admin-modal');

        this.element = null;
        this.modalEl = null;
        this.isOpen = false;
        this.isClosing = false;
        this.triggerElement = null; // Store focus return element

        // Bind methods
        this.handleEsc = this.handleEsc.bind(this);
        this.handleTab = this.handleTab.bind(this);
    }

    /**
     * Get CSS class names based on premium mode.
     */
    getClasses() {
        return {
            overlay: 'mpz-modal-overlay',
            modal: 'mpz-modal',
            header: 'mpz-modal__header',
            title: 'mpz-modal__title',
            close: 'mpz-modal__close',
            content: 'mpz-modal__content',
            tabs: 'mpz-modal-tabs',
            actions: 'mpz-modal__actions'
        };
    }

    /**
     * Render the modal structure.
     * @param {HTMLElement|string} content - The body content
     * @param {HTMLElement|Array} actions - Footer action buttons
     * @param {Array} tabs - Optional tabs configuration
     */
    render(content, actions = null, tabs = null) {
        const classes = this.getClasses();

        // Build title element
        const titleContent = [];
        if (this.options.icon && this.options.premium) {
            titleContent.push(
                el('span', { class: 'multilingual-modal__title-icon' }, this.options.icon)
            );
        }
        titleContent.push(this.options.title || '');

        this.element = el('div', {
            class: classes.overlay,
            onclick: (e) => {
                if (e.target.classList.contains('mpz-modal-overlay')) {
                    this.close();
                }
            },
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': 'mpz-modal-title'
        },
            this.modalEl = el('div', {
                class: classes.modal + (this.options.extraClass ? ` ${this.options.extraClass}` : ''),
                style: { maxWidth: this.options.width }
            },
                // Header
                el('header', { class: classes.header },
                    el('h2', {
                        id: 'mpz-modal-title',
                        class: classes.title
                    }, ...titleContent),
                    el('div', { class: 'mpz-modal__header-controls' },
                        // Header action button (optional)
                        this.options.headerAction ? el('div', { class: 'mpz-modal__header-action' }, this.options.headerAction) : null,
                        el('button', {
                            class: classes.close,
                            onclick: () => this.close(),
                            title: __('Close', 'multilingual-press-zone'),
                            'aria-label': __('Close', 'multilingual-press-zone')
                        }, '×')
                    )
                ),

                // Tabs (optional) - in premium mode, tabs are outside header
                tabs ? el('div', { class: classes.tabs, role: 'tablist' }, ...tabs) : null,

                // Content
                el('div', { class: classes.content, role: 'tabpanel' }, content),

                // Footer (Actions)
                actions ? el('div', { class: classes.actions },
                    ...(Array.isArray(actions) ? actions : [actions])
                ) : null
            )
        );

        this.container.appendChild(this.element);
    }

    /**
     * Update only the content and tabs without re-rendering the entire modal.
     * This prevents the modal pop-in animation from re-triggering on tab changes.
     * @param {HTMLElement|string} content - The new body content
     * @param {Array} tabs - Optional new tabs configuration
     */
    updateContent(content, tabs = null) {
        if (!this.modalEl) {
            // Modal not rendered yet, fall back to full render
            this.render(content, null, tabs);
            return;
        }

        const classes = this.getClasses();

        // Update tabs if provided
        const tabsContainer = this.modalEl.querySelector(`.${classes.tabs.split(' ')[0]}`);
        if (tabs) {
            if (tabsContainer) {
                clear(tabsContainer);
                tabs.forEach(tab => tabsContainer.appendChild(tab));
            } else {
                // Insert tabs after header if they don't exist
                const header = this.modalEl.querySelector('header');
                if (header) {
                    const newTabsEl = el('div', { class: classes.tabs, role: 'tablist' }, ...tabs);
                    header.after(newTabsEl);
                }
            }
        }

        // Update content
        const contentContainer = this.modalEl.querySelector(`.mpz-modal__content`);
        if (contentContainer) {
            clear(contentContainer);
            if (typeof content === 'string') {
                contentContainer.innerHTML = content;
            } else if (content) {
                contentContainer.appendChild(content);
            }
        }
    }

    /**
     * Update only the footer actions without re-rendering the entire modal.
     * @param {HTMLElement|Array} actions - New action buttons
     */
    updateActions(actions) {
        if (!this.modalEl) return;
        const container = this.modalEl.querySelector('.mpz-modal__actions');
        if (!container) return;
        while (container.firstChild) container.removeChild(container.firstChild);
        (Array.isArray(actions) ? actions : [actions]).forEach(a => {
            if (a) container.appendChild(a);
        });
    }

    /**
     * Open the modal.
     */
    open() {
        if (!this.element) {
            console.warn('Modal content not rendered. Call render() first.');
            return;
        }

        // Store current focus for return
        this.triggerElement = document.activeElement;

        this.container.style.display = 'block';
        this.isOpen = true;
        this.isClosing = false;
        document.body.style.overflow = 'hidden'; // Prevent background scroll
        document.addEventListener('keydown', this.handleEsc);
        document.addEventListener('keydown', this.handleTab);

        // Focus first focusable element
        requestAnimationFrame(() => {
            const firstInput = this.element.querySelector('input, select, textarea, button:not(.mpz-modal__close)');
            if (firstInput) {
                firstInput.focus();
            }
        });
    }

    /**
     * Close the modal with optional animation.
     */
    close() {
        if (this.isClosing) return;

        this.finalizeClose();
    }

    /**
     * Complete the close operation.
     */
    finalizeClose() {
        if (this.element && this.element.parentNode) {
            this.element.remove();
        }

        // Only hide container if no other modals are present
        if (this.container.children.length === 0) {
            this.container.style.display = 'none';
            document.body.style.overflow = '';
        }

        this.isOpen = false;
        this.isClosing = false;
        this.element = null;
        this.modalEl = null;

        document.removeEventListener('keydown', this.handleEsc);
        document.removeEventListener('keydown', this.handleTab);

        // Return focus to trigger element
        if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
            this.triggerElement.focus();
        }

        if (typeof this.options.onClose === 'function') {
            this.options.onClose();
        }
    }

    /**
     * Handle Escape key to close.
     */
    handleEsc(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    /**
     * Handle Tab key for focus trap.
     */
    handleTab(e) {
        if (e.key !== 'Tab' || !this.isOpen || !this.element) return;

        const focusableElements = this.element.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstEl = focusableElements[0];
        const lastEl = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab: going backwards
            if (document.activeElement === firstEl) {
                e.preventDefault();
                lastEl.focus();
            }
        } else {
            // Tab: going forward
            if (document.activeElement === lastEl) {
                e.preventDefault();
                firstEl.focus();
            }
        }
    }

    /**
     * Update the title dynamically.
     */
    setTitle(title) {
        this.options.title = title;
        if (this.element) {
            const h2 = this.element.querySelector('h2');
            if (h2) h2.textContent = title;
        }
    }
}
